import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Box, Typography, Select, MenuItem } from '@mui/material';
import client from '../../utils/contentfulClient';
import { toTitleCase } from '../../utils/casing';

const BlogCategoryNav = ({ activeCategory = 'all', activeYear = null }) => {
  const navigate = useNavigate();
  const [categoryCounts, setCategoryCounts] = useState({});
  const [totalCount, setTotalCount] = useState(0);
  const [years, setYears] = useState([]);

  useEffect(() => {
    let isMounted = true;

    const getNavData = async () => {
      try {
        const response = await client.getEntries({
          content_type: 'blogPost',
          select: ['fields.category', 'fields.blogDate'],
          limit: 1000,
        });

        if (!isMounted) return;

        const counts = {};
        response.items.forEach((item) => {
          const category = item.fields?.category;
          if (category) counts[category] = (counts[category] || 0) + 1;
        });

        const finalYearValues = [
          ...new Set(
            response.items
              .map((item) => item.fields?.blogDate?.substring(0, 4))
              .filter(Boolean)
          ),
        ];

        setCategoryCounts(counts);
        setTotalCount(response.items.length);
        setYears(finalYearValues.sort().reverse());
      } catch (err) {
        // ignore
      }
    };

    getNavData();

    return () => {
      isMounted = false;
    };
  }, []);

  const categories = ['all', ...Object.keys(categoryCounts).sort()];

  const handleYearChange = (e) => {
    const year = e.target.value;
    navigate(
      year
        ? `/blog/SortByYear/?Year=${year}`
        : activeCategory !== 'all'
          ? `/blog/${activeCategory}`
          : '/blog/all'
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        mb: 3,
        pb: 1.5,
        borderBottom: '1px solid #E3E6E8',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          overflowX: 'auto',
          overflowY: 'hidden',
          whiteSpace: 'nowrap',
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          flex: '1 1 320px',
          minWidth: 0,
        }}
      >
        {categories.map((category) => {
          const isActive = activeCategory === category;
          const count =
            category === 'all' ? totalCount : categoryCounts[category] || 0;
          return (
            <Box
              key={category}
              component={Link}
              to={`/blog/${category}`}
              sx={{
                textDecoration: 'none',
                flexShrink: 0,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                px: '14px',
                py: '8px',
                borderRadius: '20px',
                backgroundColor: isActive ? '#162F3B' : '#F2F3F3',
                color: isActive ? '#FFFFFF' : '#1E354E',
                transition: 'background-color 0.15s ease',
                '&:hover': {
                  backgroundColor: isActive ? '#162F3B' : '#E3E6E8',
                },
              }}
            >
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 600,
                  fontSize: '0.813rem',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  color: 'inherit',
                }}
              >
                {category === 'all' ? 'All' : toTitleCase(category)}
              </Typography>
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontWeight: 500,
                  fontSize: '0.688rem',
                  lineHeight: 1,
                  opacity: 0.7,
                  color: 'inherit',
                }}
              >
                {count}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Select
        value={activeYear || ''}
        onChange={handleYearChange}
        displayEmpty
        size="small"
        variant="outlined"
        MenuProps={{
          slotProps: {
            paper: {
              sx: {
                mt: '6px',
                border: '1px solid #A2ACB1',
                borderRadius: '12px',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.06)',
                '& .MuiMenuItem-root': {
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '0.875rem',
                  color: '#162F3B',
                },
              },
            },
          },
        }}
        sx={{
          minWidth: 130,
          flexShrink: 0,
          fontFamily: 'Inter, sans-serif',
          fontSize: '0.875rem',
          color: '#162F3B',
          borderRadius: '12px',
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#A2ACB1',
            borderRadius: '12px',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#A2ACB1',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#A2ACB1',
          },
          '& .MuiSelect-select': { py: '10px', px: '14px' },
          '& .MuiSelect-icon': { color: '#162F3B' },
        }}
      >
        <MenuItem value="">All Years</MenuItem>
        {years.map((year) => (
          <MenuItem key={year} value={year}>
            {year}
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default BlogCategoryNav;
